import { Component } from 'react';
import { captureException } from '../config/sentry';

/**
 * ErrorBoundary - Catches React errors and displays fallback UI
 * Prevents white screen of death in production
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console in development
        if (import.meta.env.DEV) {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }

        // Send to Sentry in production
        captureException(error, {
            errorInfo,
            componentStack: errorInfo.componentStack
        });

        this.setState({
            error,
            errorInfo
        });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
        // Reload the page
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
                    <div className="max-w-2xl w-full">
                        {/* Error container */}
                        <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl border border-red-500/20 shadow-2xl p-8 md:p-12">
                            {/* Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center">
                                    <svg
                                        className="w-10 h-10 text-red-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                        />
                                    </svg>
                                </div>
                            </div>

                            {/* Title */}
                            <h1 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
                                Something Went Wrong
                            </h1>

                            {/* Description */}
                            <p className="text-gray-300 text-center mb-8 text-lg">
                                We're sorry, but something unexpected happened. Our team has been notified and we're working on a fix.
                            </p>

                            {/* Error details (development only) */}
                            {import.meta.env.DEV && this.state.error && (
                                <div className="mb-8 bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                                    <p className="text-red-400 font-mono text-sm mb-2 font-semibold">
                                        {this.state.error.toString()}
                                    </p>
                                    {this.state.errorInfo && (
                                        <details className="mt-2">
                                            <summary className="text-gray-400 text-sm cursor-pointer hover:text-gray-300">
                                                Component Stack
                                            </summary>
                                            <pre className="text-gray-500 text-xs mt-2 overflow-x-auto">
                                                {this.state.errorInfo.componentStack}
                                            </pre>
                                        </details>
                                    )}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={this.handleReset}
                                    className="px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/20 hover:shadow-primary-500/40 transition-all duration-200 hover:-translate-y-0.5"
                                >
                                    Refresh Page
                                </button>
                                <button
                                    onClick={() => window.location.href = '/dashboard'}
                                    className="px-8 py-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200"
                                >
                                    Go to Homepage
                                </button>
                            </div>

                            {/* Help text */}
                            <p className="text-gray-500 text-sm text-center mt-8">
                                If this problem persists, please contact support at{' '}
                                <a
                                    href="mailto:support@proprscout.com"
                                    className="text-primary-400 hover:text-primary-300 underline"
                                >
                                    support@proprscout.com
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
